import React from "react";
import { useNavigate } from "react-router-dom";
import { FaPhone, FaBars, FaTimes, FaBullseye, FaEye, FaHandsHelping } from "react-icons/fa";
import logo from "../assets/logoelite.jpeg";
import mission from "../assets/Mission.jpg";
import Vision from "../assets/Vision.jpg";
import commitment from "../assets/Commitment.jpg";

const About = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = React.useState(false);

  const handleLoginClick = () => navigate("/login");

  const items = [
    {
      id: 1,
      title: "Our Mission",
      subtitle: "Empower every learner to succeed",
      desc:
        "To create an inclusive, outcome-driven learning ecosystem that helps every student realise their potential. We combine curriculum-aligned preparatory material, micro-schedules and continuous assessment to build confidence, consistency and competence.",
      img: mission,
      accent: "#6A1B9A",
      icon: <FaBullseye />
    },
    {
      id: 2,
      title: "Our Vision",
      subtitle: "Small steps. Big results.",
      desc:
        "Inspired by the Micro Schedule approach, we envision classrooms where daily bite-sized learning, targeted practice and teacher-guided feedback create measurable progress — transforming long-term outcomes through short, consistent habits.",
      img: Vision,
      accent: "#0D47A1",
      icon: <FaEye />
    },
    {
      id: 3,
      title: "Our Commitment",
      subtitle: "Quality, privacy and partnership",
      desc:
        "We are committed to secure data handling, continuous improvement and close collaboration with schools. Our promise: reliable technology, responsive support and tangible impact for students, teachers and parents.",
      img: commitment,
      accent: "#00695C",
      icon: <FaHandsHelping />
    }
  ];

  const css = `
    .about-page { font-family: Inter, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial; color:#24303b; min-height:100vh; display:flex; flex-direction:column; }
    .header { display:flex; justify-content:space-between; align-items:center; padding:14px 20px; background:#003366; color:#fff; position:sticky; top:0; z-index:80; }
    .logo-container { display:flex; align-items:center; gap:10px; }
    .logo { height:44px; border-radius:6px; object-fit:cover; }
    .logo-text { font-weight:700; font-size:18px; color:#fff; }
    .menu-icon { color:#fff; font-size:20px; cursor:pointer; display:none; background:none; border:none; }
    .nav-links { display:flex; gap:14px; align-items:center; }
    .nav-links.open { display:flex; }
    .nav-btn-link { background:none; border:none; color:#fff; font-weight:600; font-size:15px; cursor:pointer; padding:6px 8px; }
    .header-right { display:flex; align-items:center; gap:12px; }
    .phone-btn { color:#fff; text-decoration:none; display:flex; align-items:center; gap:8px; font-weight:600; }
    .login-btn { background:#ff6600; color:#fff; border:none; padding:8px 12px; border-radius:8px; cursor:pointer; font-weight:700; }

    .about-hero { background: linear-gradient(135deg, rgba(81,45,168,0.9), rgba(24,118,210,0.85)), url('https://images.unsplash.com/photo-1496307042754-b4aa456c4a2d?auto=format&fit=crop&w=1600&q=60') center/cover no-repeat; color:#fff; padding:64px 20px; text-align:center; }
    .about-hero .inner { max-width:1100px; margin:0 auto; }
    .about-hero h1 { font-size:40px; margin-bottom:12px; letter-spacing:0.2px; }
    .about-hero p { max-width:900px; margin:0 auto; background:rgba(255,255,255,0.06); padding:16px; border-radius:8px; line-height:1.6; }

    .about-main { max-width:1200px; margin:40px auto; padding:0 18px 40px; display:flex; flex-direction:column; gap:28px; }
    .about-row { display:flex; gap:28px; align-items:center; background:#fff; border-radius:12px; box-shadow:0 10px 30px rgba(16,24,40,0.06); overflow:hidden; }
    .about-image { flex:0 0 45%; min-height:220px; max-height:420px; overflow:hidden; }
    .about-image img { width:100%; height:100%; object-fit:cover; display:block; transition:transform .6s ease, filter .6s ease; }
    .about-image img:hover { transform:scale(1.03); filter:saturate(1.05); }
    .about-content { flex:1; padding:26px; display:flex; flex-direction:column; justify-content:center; }
    .accent { display:flex; align-items:center; gap:12px; margin-bottom:10px; }
    .accent-icon { width:52px; height:52px; display:inline-flex; align-items:center; justify-content:center; border-radius:10px; font-size:20px; color:var(--accent); background:var(--accent)22; }
    .about-title { margin:0; font-size:22px; color:var(--accent); font-weight:800; }
    .about-sub { margin-top:6px; color:#6b7280; font-weight:600; }
    .about-desc { margin-top:12px; color:#374151; line-height:1.75; font-size:15px; }
    .about-badges { margin-top:16px; display:flex; gap:12px; }

    .badge-primary { background:var(--accent); color:#fff; padding:9px 14px; border-radius:8px; font-weight:700; }
    .badge-muted { border:1px solid #e6e6e6; padding:9px 14px; border-radius:8px; color:#374151; }

    .site-footer { padding:22px; text-align:center; color:#6b7280; margin-top:auto; background:#f7f7f7; }

    @media (max-width: 900px) {
      .menu-icon { display:block; }
      .nav-links { display:none; position:absolute; top:64px; right:16px; background:#003366; padding:12px; border-radius:8px; flex-direction:column; gap:8px; }
      .nav-links.open { display:flex; width:200px; }
      .about-row { flex-direction:column; }
      .about-image { width:100%; max-height:300px; min-height:200px; flex-basis:auto; }
      .about-content { padding:18px; }
      .about-hero { padding:48px 16px; }
      .about-hero h1 { font-size:28px; }
    }
  `;

  // helper to navigate and close mobile menu when link clicked
  const go = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  return (
    <div className="about-page">
      <style>{css}</style>

      <header className="header">
        <div className="logo-container">
          <img src={logo} alt="Elite Academy Logo" className="logo" />
          <span className="logo-text">Elite Academy</span>
        </div>

        <button
          className="menu-icon"
          aria-label="Toggle menu"
          onClick={() => setMenuOpen((s) => !s)}
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>

        <div className={`nav-links ${menuOpen ? "open" : ""}`}>
          <button className="nav-btn-link" onClick={() => go("/")}>HOME</button>
          <button className="nav-btn-link" onClick={() => go("/about")}>ABOUT US</button>
          <button className="nav-btn-link" onClick={() => go("/features")}>FEATURES</button>
          <button className="nav-btn-link" onClick={() => go("/contact")}>CONTACT US</button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
           <button
            className="sample-btn"
            onClick={() => navigate("/sample-copy")}
            aria-label="View sample copies"
            style={{
              marginLeft: 12,
              background: "linear-gradient(90deg,#ff7a18,#af002d)",
              color: "#fff",
              border: "none",
              padding: "8px 14px",
              borderRadius: 8,
              boxShadow: "0 6px 18px rgba(175,0,45,0.18)",
              cursor: "pointer",
              fontWeight: 700,
              letterSpacing: 0.2,
            }}
          >
            Sample Copy
          </button>
          <button className="login-btn" onClick={handleLoginClick}>LOGIN</button>
        </div>
      </header>

      <section className="about-hero">
        <div className="inner">
          <h1>About Elite Academy</h1>
          <p>
            We combine modern pedagogy, bite-sized learning and real-time assessment to create measurable learning progress.
            Below are the pillars that drive everything we build — Mission, Vision and Commitment.
          </p>
        </div>
      </section>

      <main className="about-main">
        {items.map((it) => (
          <section key={it.id} className="about-row" style={{ ['--accent']: it.accent }}>
            <div className="about-image">
              <img src={it.img} alt={it.title} />
            </div>

            <div className="about-content">
              <div className="accent" style={{ ['--accent']: it.accent }}>
                <div className="accent-icon" style={{ color: it.accent, background: `${it.accent}22` }}>{it.icon}</div>
                <div>
                  <h2 className="about-title" style={{ color: it.accent }}>{it.title}</h2>
                  <div className="about-sub">{it.subtitle}</div>
                </div>
              </div>

              <p className="about-desc">{it.desc}</p>

              <div className="about-badges">
                <div className="badge-primary" style={{ background: it.accent }}>Why it matters</div>
                <div className="badge-muted">Real classroom impact • Scalable • Teacher friendly</div>
              </div>
            </div>
          </section>
        ))}
      </main>

      <footer className="footer">
        <p>&copy; {new Date().getFullYear()} Elite Academy. All Rights Reserved.</p>
      </footer>
    </div>
  );
};

export default About;